import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiPhone, FiMail, FiMapPin, FiClock } from 'react-icons/fi';
import useIsMobile from '../utils/useIsMobile';

const footerLinks = {
  Company: [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact Us', path: '/contact' },
  ],
  Services: [
    { name: 'Internet', path: '/#services' },
    { name: 'Cable TV', path: '/#services' },
    { name: 'Wi-Fi', path: '/#services' },
    { name: 'Wireless', path: '/#services' },
  ],
  Resources: [
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms & Conditions', path: '/terms' },
    { name: 'Cookie Policy', path: '/cookies' },
    { name: 'Sales & Enrollment', path: '/contact' },
  ],
};

const Footer = () => {
  const isMobile = useIsMobile(768);
  const isTablet = useIsMobile(1024);
  const location = useLocation();

  // Smoothly scroll to the Services section (offsetting the fixed 72px navbar).
  const scrollToServices = () => {
    const el = document.getElementById('services');
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 72;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // Footer link click handler. Anchor links (e.g. /#services) reliably scroll to
  // the Services section on every click — including repeated clicks and from
  // other pages — instead of relying on a hash change that may not re-fire.
  const handleLinkClick = (e, path) => {
    if (path.includes('#')) {
      if (location.pathname === '/') {
        e.preventDefault();
        scrollToServices();
      }
      // On another page: let the Link navigate to '/#services';
      // App's ScrollToHash handles the scroll once Home mounts.
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const gridCols = isMobile
    ? '1fr'
    : isTablet
      ? '1fr 1fr'
      : '1.4fr repeat(3, 1fr)';

  return (
    <footer style={{
      background: 'linear-gradient(165deg, #0a1128 0%, #0f172a 100%)',
      paddingTop: 120,
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>
      <div className="max-content-width" style={{ width: '100%', margin: '0 auto', padding: isMobile ? '0 var(--cc-px) 40px' : '0 var(--cc-px) 48px' }}>

        {/* Top row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: gridCols,
          gap: isMobile ? 32 : isTablet ? 32 : 48,
          marginBottom: isMobile ? 36 : 56,
          paddingTop: 60,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          alignItems: 'flex-start',
        }}>

          {/* Brand column */}
          <div>
            {/* Logo */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ lineHeight: 1 }}>
                <span style={{ display: 'block', color: '#fff', fontWeight: 800, fontSize: '1.35rem', letterSpacing: '-0.01em', fontFamily: "'Plus Jakarta Sans','DM Sans','Segoe UI',sans-serif" }}>InternetTV</span>
                <span style={{ display: 'block', color: '#60a5fa', fontWeight: 800, fontSize: '0.9rem', letterSpacing: '0.14em', textTransform: 'uppercase', fontFamily: "'Plus Jakarta Sans','DM Sans','Segoe UI',sans-serif" }}>Service</span>
              </div>
            </div>

            <p style={{ color: 'rgba(255,255,255,0.95)', fontSize: '1.02rem', lineHeight: 1.95, margin: '0 0 20px', maxWidth: 540, fontWeight: 700 }}>
              We are an AUTHORIZED DEALER for Internet &amp; TV Services
            </p>

            {/* Contact details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { icon: FiMapPin, text: 'Address: 13800 Coppermine Rd, Herndon, VA 20171', href: null },
                { icon: FiPhone, text: 'Phone: +1 (888) 880-2585', href: 'tel:+18888802585' },
                { icon: FiMail, text: 'Email: contact@internettvservice.com', href: 'mailto:contact@internettvservice.com' },
                { icon: FiClock, text: 'Hours: Mon–Fri: 9AM–6PM, Sat: 9AM–5PM, Sun: closed', href: null },
              ].map(({ icon: Icon, text, href }, i) => {
                const content = (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <Icon style={{ width: 16, height: 16, color: '#60a5fa', flexShrink: 0, marginTop: 2 }} />
                    <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem' }}>{text}</span>
                  </div>
                );
                return href ? (
                  <a key={i} href={href} style={{ textDecoration: 'none', transition: 'opacity 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >{content}</a>
                ) : <div key={i}>{content}</div>;
              })}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks).map(([heading, links]) => (
            <div key={heading} style={{ textAlign: isMobile ? 'center' : 'left' }}>
              <h6 style={{ color: '#fff', fontWeight: 800, fontSize: '0.95rem', margin: '0 0 18px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{heading}</h6>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {links.map((l, i) => (
                  <li key={i}>
                    <Link to={l.path} style={{
                      color: 'rgba(255,255,255,0.9)', fontSize: '1rem', textDecoration: 'none',
                      transition: 'color 0.15s',
                    }}
                      onClick={(e) => handleLinkClick(e, l.path)}
                      onMouseEnter={e => e.currentTarget.style.color = '#93c5fd'}
                      onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.9)'}
                    >{l.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Compliance disclaimer */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 24 }}>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', lineHeight: 1.85 }}>
            <strong style={{ color: 'rgba(255,255,255,0.8)' }}>Disclaimer:</strong> InternetTVService is a DBA of
            CyberCloud LLC and operates as an independent Authorized Dealer for Internet and TV services — not an
            internet or TV service provider. We are not affiliated with, endorsed by, or sponsored by any provider,
            and this website is not the official site of any provider. All company and brand names, trademarks, and
            logos are the property of their respective owners and are used for identification only. Plans, pricing,
            speeds, channel lineups, and availability vary by location and provider and are subject to change;
            availability is confirmed by address, and speeds are not guaranteed. Advertised offers may be
            promotional, may change without notice, may require eligibility or agreements, and may exclude taxes,
            fees, and equipment charges. All services are subject to the applicable provider's terms and conditions.
            We assist with plan comparison, enrollment, and account setup — technical service and repairs are handled
            by the provider. We may receive compensation from participating providers.
          </p>
          <p style={{ margin: '14px 0 0', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', lineHeight: 1.85 }}>
            <strong style={{ color: 'rgba(255,255,255,0.7)' }}>Advertising transparency:</strong> We advertise our own
            dealer services and assist with sales and enrollment. For information on how Google handles advertisers that
            work with third parties, see Google's{' '}
            <a
              href="https://support.google.com/adspolicy/answer/9457109"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'rgba(147,197,253,0.95)', textDecoration: 'underline' }}
            >
              Advertiser guide: Working with third parties
            </a>.{' '}
            <strong style={{ color: 'rgba(255,255,255,0.7)' }}>Accessibility:</strong> If you have difficulty using any
            part of this website, call{' '}
            <a href="tel:+18888802585" style={{ color: 'rgba(147,197,253,0.95)', textDecoration: 'underline' }}>+1 (888) 880-2585</a>{' '}
            or email{' '}
            <a href="mailto:contact@internettvservice.com" style={{ color: 'rgba(147,197,253,0.95)', textDecoration: 'underline' }}>contact@internettvservice.com</a>{' '}
            and we'll help.
          </p>
        </div>

        {/* Bottom bar */}
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.06)',
          marginTop: 22,
          paddingTop: 22,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          flexDirection: isMobile ? 'column' : 'row',
          textAlign: isMobile ? 'center' : 'left',
        }}>
          <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.98rem', margin: 0, fontWeight: 600 }}>
© 2026 CyberCloud LLC (DBA InternetTVService) — internettvservice.com
          </p>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
            {[['Privacy Policy', '/privacy'], ['Terms', '/terms'], ['Cookies', '/cookies'], ['Contact', '/contact']].map(([name, path]) => (
              <Link key={name} to={path} style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', textDecoration: 'none', transition: 'color 0.15s' }}
                onClick={(e) => handleLinkClick(e, path)}
                onMouseEnter={e => e.currentTarget.style.color = '#93c5fd'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.85)'}
              >{name}</Link>
            ))}
          </div>
        </div>

        {/* Powered by */}
        <p style={{
          margin: 0,
          paddingTop: 16,
          paddingBottom: isMobile ? 24 : 0,
          textAlign: 'center',
          color: 'rgba(255,255,255,0.5)',
          fontSize: '0.8rem',
          fontWeight: 500,
        }}>
          Powered by <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 700 }}>InternetTVService</span>
        </p>
      </div>
    </footer>
  );
};

export default Footer;
